import { useState, useMemo } from "react";
import { menuCategories, menuItems, restaurantInfo } from "../mock";
import { DietTags } from "../components/DietTags";
import { ExternalLink, Search, Sparkles } from "lucide-react";
import SEO, { menuSchema, breadcrumbSchema, faqSchema } from "../components/SEO";
import { useLang } from "../context/LanguageContext";
import FAQ from "../components/FAQ";
import { faqs } from "../data/faqs";
import { SEO_STRINGS } from "../i18n/strings";

export default function Menu() {
  const { lang } = useLang();
  const [active, setActive] = useState("all");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    let items = active === "all" ? menuItems : menuItems.filter((i) => i.category === active);
    if (query.trim()) {
      const q = query.toLowerCase();
      items = items.filter(
        (i) =>
          i.name.toLowerCase().includes(q) ||
          (i.nameEs && i.nameEs.toLowerCase().includes(q)) ||
          i.description.toLowerCase().includes(q) ||
          (i.descriptionEs && i.descriptionEs.toLowerCase().includes(q))
      );
    }
    return items;
  }, [active, query]);

  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach((item) => {
      if (!map[item.category]) map[item.category] = [];
      map[item.category].push(item);
    });
    return map;
  }, [filtered]);

  const showEn = lang === "en" || lang === "both";
  const showEs = lang === "es" || lang === "both";
  const both = lang === "both";

  const strings = {
    en: {
      eyebrow: "La Carta",
      title: "Our Menu",
      tagline: "Fresh ingredients. Handmade tortillas. Traditional family recipes.",
      searchPh: "Search dishes…",
      allTab: "All",
      empty: "No dishes match",
      emptyTip: "Try a different search or browse a category.",
      ctaEyebrow: "Every recipe, made fresh today",
      ctaTitle: "Order direct — the way it should be.",
      ctaCopy: "Skip third-party fees. Order straight from our kitchen for pickup or delivery, and earn rewards on every visit.",
      ctaBtn: "Order Online",
    },
    es: {
      eyebrow: "La Carta",
      title: "Nuestro Menú",
      tagline: "Ingredientes frescos. Tortillas hechas a mano. Recetas de familia.",
      searchPh: "Buscar platos…",
      allTab: "Todo",
      empty: "Ningún plato coincide con",
      emptyTip: "Prueba otra búsqueda o revisa las categorías.",
      ctaEyebrow: "Cada receta, hecha hoy",
      ctaTitle: "Ordena directo — como debe ser.",
      ctaCopy: "Sáltate las tarifas de terceros. Ordena directo desde nuestra cocina para recoger o a domicilio, y gana recompensas en cada visita.",
      ctaBtn: "Ordenar en línea",
    },
  };
  const t = strings[lang === "es" ? "es" : "en"];

  return (
    <>
      <SEO
        title={SEO_STRINGS.menu.title.en}
        titleEs={SEO_STRINGS.menu.title.es}
        description={SEO_STRINGS.menu.description.en}
        descriptionEs={SEO_STRINGS.menu.description.es}
        path="/menu"
        keywords="Nina's Taqueria menu, Mexican menu Los Gatos, tacos Los Gatos, burritos Bay Area, authentic Mexican menu, taqueria menu, menú en español, churros Los Gatos"
        keywordsEs="menú Nina's Taqueria, menú mexicano Los Gatos, tacos Los Gatos, burritos Bay Area, menú mexicano auténtico, menú taquería, menu in Spanish, churros Los Gatos"
        schema={{
          "@context": "https://schema.org",
          "@graph": [
            menuSchema(menuItems, menuCategories),
            faqSchema(faqs, lang),
            breadcrumbSchema([
              { name: lang === "es" ? "Inicio" : "Home", path: "/" },
              { name: lang === "es" ? "Menú" : "Menu", path: "/menu" },
            ]),
          ],
        }}
      />
      <main className="pt-28 pb-24 bg-cream-50 min-h-screen">
        {/* Header */}
        <section className="max-w-7xl mx-auto px-6 pt-10 pb-14 text-center">
          <div className="font-hand text-2xl text-brand mb-2">{t.eyebrow}</div>
          <h1 className="font-display text-5xl md:text-7xl font-semibold leading-[1] text-granite-dark">
            {t.title}
          </h1>
          <p className="mt-5 text-granite-dark font-display text-lg md:text-xl max-w-2xl mx-auto leading-snug">
            {t.tagline}
          </p>
          <p className="mt-3 text-granite/55 max-w-2xl mx-auto text-xs md:text-sm">
            {restaurantInfo.priceDisclaimer}
          </p>

          {/* Search */}
          <div className="mt-8 max-w-md mx-auto relative">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-granite/40" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t.searchPh}
              data-testid="menu-search-input"
              className="w-full pl-11 pr-4 py-3.5 rounded-full bg-cream border border-cream-100 focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none text-sm text-granite-dark placeholder:text-granite/40 transition-all"
            />
          </div>
        </section>

        {/* Sticky category tabs */}
        <div className="sticky top-[72px] z-30 bg-cream-50/95 backdrop-blur-md border-y border-cream-100 mb-10">
          <div className="max-w-7xl mx-auto px-6 overflow-x-auto">
            <div className="flex gap-1 py-3 min-w-max">
              <button
                onClick={() => setActive("all")}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  active === "all"
                    ? "bg-brand text-cream shadow-md"
                    : "text-granite/70 hover:bg-cream-100"
                }`}
              >
                {t.allTab}
              </button>
              {menuCategories.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setActive(c.id)}
                  data-testid={`menu-cat-${c.id}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                    active === c.id
                      ? "bg-brand text-cream shadow-md"
                      : "text-granite/70 hover:bg-cream-100"
                  }`}
                >
                  {lang === "es" ? c.nameEs : c.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Menu sections */}
        <div className="max-w-7xl mx-auto px-6">
          {menuCategories
            .filter((c) => grouped[c.id] && grouped[c.id].length > 0)
            .map((cat) => (
              <section key={cat.id} id={cat.id} className="mb-20">
                <div className="mb-10">
                  {both ? (
                    <>
                      <div className="font-hand text-xl text-brand mb-1">{cat.subtitle}</div>
                      <h2 className="font-display text-4xl md:text-5xl font-semibold text-granite-dark flex items-center gap-3">
                        {cat.name}
                        <span className="flex-1 h-[1px] bg-granite/10 max-w-[140px]" />
                      </h2>
                      <div className="mt-2 font-display text-2xl md:text-3xl italic text-granite/55">{cat.nameEs}</div>
                      <div className="mt-1 text-sm text-granite/55 max-w-3xl">{cat.subtitleEs}</div>
                    </>
                  ) : (
                    <>
                      <div className="font-hand text-xl text-brand mb-1">
                        {showEs && !showEn ? cat.subtitleEs : cat.subtitle}
                      </div>
                      <h2 className="font-display text-4xl md:text-5xl font-semibold text-granite-dark flex items-center gap-3">
                        {showEs && !showEn ? cat.nameEs : cat.name}
                        <span className="flex-1 h-[1px] bg-granite/10 max-w-[140px]" />
                      </h2>
                    </>
                  )}
                </div>

                <div className="grid md:grid-cols-2 gap-x-10 gap-y-8">
                  {grouped[cat.id].map((item) => (
                    <div
                      key={item.id}
                      data-testid={`menu-item-${item.id}`}
                      className="group flex gap-5 items-start pb-6 border-b border-dashed border-granite/10"
                    >
                      {item.image && (
                        <div className="w-24 h-24 md:w-28 md:h-28 rounded-2xl overflow-hidden shrink-0 shadow-md">
                          <img
                            src={item.image}
                            alt={item.name}
                            loading="lazy"
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                          />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-3 mb-1.5">
                          <h3 className="font-display text-xl font-semibold text-granite-dark flex items-center gap-2">
                            {item.tags?.includes("star") && (
                              <Sparkles className="w-4 h-4 text-brand" />
                            )}
                            {showEs && !showEn ? item.nameEs : item.name}
                          </h3>
                          <span className="font-display text-lg text-brand font-semibold shrink-0">
                            ${item.price.toFixed(2)}{item.priceSuffix || ""}
                          </span>
                        </div>

                        {both && (
                          <div className="text-sm text-granite/50 italic mb-1.5">{item.nameEs}</div>
                        )}

                        <p className="text-sm text-granite/70 leading-relaxed mb-2">
                          {showEs && !showEn ? item.descriptionEs : item.description}
                        </p>
                        {both && item.descriptionEs && (
                          <p className="text-sm text-granite/50 italic leading-relaxed mb-3 border-l-2 border-brand/25 pl-3">
                            {item.descriptionEs}
                          </p>
                        )}
                        <DietTags tags={item.tags} />
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))}

          {filtered.length === 0 && (
            <div className="text-center py-24">
              <div className="font-hand text-2xl text-brand mb-2">¡Ay, caramba!</div>
              <p className="font-display text-2xl text-granite-dark">{t.empty} “{query}”</p>
              <p className="text-granite/60 mt-2">{t.emptyTip}</p>
            </div>
          )}
        </div>

        {/* Order CTA */}
        <section className="max-w-4xl mx-auto px-6 mt-16">
          <div className="rounded-3xl bg-granite-dark text-cream p-10 md:p-14 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-noise opacity-20" />
            <div className="relative">
              <div className="font-hand text-2xl text-brand-light mb-2">{t.ctaEyebrow}</div>
              <h3 className="font-display text-3xl md:text-4xl font-semibold">{t.ctaTitle}</h3>
              <p className="text-cream/70 mt-3 max-w-md mx-auto">{t.ctaCopy}</p>
              <a
                href={restaurantInfo.externalLinks.orderOnline}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="menu-order-cta"
                className="mt-6 inline-flex items-center gap-2 bg-brand hover:bg-brand-dark text-cream px-8 py-4 rounded-full font-medium transition-all shadow-xl hover:-translate-y-0.5"
              >
                {t.ctaBtn} <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <FAQ
          eyebrow={lang === "es" ? "Preguntas" : "Questions"}
          title={lang === "es" ? "Preguntas frecuentes" : "Frequently asked"}
          subtitle={lang === "es" ? "Todo lo que quisieras saber antes de visitarnos en Los Gatos, CA." : "Everything you might want to know before you visit Nina's Taqueria in Los Gatos, CA."}
        />
      </main>
    </>
  );
}
